import { PoseLandmarker, FilesetResolver, type PoseLandmarkerResult, type NormalizedLandmark } from '@mediapipe/tasks-vision'
import { POSE_LM, type PoseFrame } from '@ar-suite/core'

export interface PoseTrackerOptions {
  wasmPath: string
  /** URL of pose_landmarker_lite.task (or full/heavy). */
  modelPath: string
  delegate: 'GPU' | 'CPU'
  minPoseDetectionConfidence: number
  minPosePresenceConfidence: number
  minTrackingConfidence: number
}

export const POSE_TRACKER_DEFAULTS: PoseTrackerOptions = {
  wasmPath: '/wasm',
  modelPath: '/models/pose_landmarker_lite.task',
  delegate: 'GPU',
  minPoseDetectionConfidence: 0.5,
  minPosePresenceConfidence: 0.5,
  minTrackingConfidence: 0.5,
}

export interface PoseTrackerResult {
  pose: PoseFrame | null
  inferenceMs: number
}

/**
 * MediaPipe Pose Landmarker wrapper (33 body points incl. real ear keypoints 7/8).
 * Used for ear-accurate earring placement now; shoulders/necklace and wrist/watch later.
 */
export class PoseTracker {
  readonly options: PoseTrackerOptions
  private landmarker: PoseLandmarker | null = null
  private lastTimestamp = -1
  private frame: PoseFrame | null = null

  constructor(options: Partial<PoseTrackerOptions> = {}) {
    this.options = { ...POSE_TRACKER_DEFAULTS, ...options }
  }

  get ready(): boolean {
    return this.landmarker !== null
  }

  async init(): Promise<void> {
    if (this.landmarker) return
    const vision = await FilesetResolver.forVisionTasks(this.options.wasmPath)
    const create = (delegate: 'GPU' | 'CPU') =>
      PoseLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: this.options.modelPath, delegate },
        runningMode: 'VIDEO',
        numPoses: 1,
        minPoseDetectionConfidence: this.options.minPoseDetectionConfidence,
        minPosePresenceConfidence: this.options.minPosePresenceConfidence,
        minTrackingConfidence: this.options.minTrackingConfidence,
      })
    try {
      this.landmarker = await create(this.options.delegate)
    } catch (e) {
      if (this.options.delegate === 'GPU') {
        console.warn('[ar-suite] pose GPU delegate failed, falling back to CPU', e)
        this.landmarker = await create('CPU')
      } else throw e
    }
  }

  detect(video: HTMLVideoElement | HTMLCanvasElement | ImageBitmap | OffscreenCanvas, timestampMs: number): PoseTrackerResult {
    if (!this.landmarker) throw new Error('PoseTracker.init() has not completed')
    if (timestampMs <= this.lastTimestamp) timestampMs = this.lastTimestamp + 1
    this.lastTimestamp = timestampMs
    const t0 = performance.now()
    const result: PoseLandmarkerResult = this.landmarker.detectForVideo(video, timestampMs)
    const inferenceMs = performance.now() - t0
    const lms = result.landmarks[0]
    if (!lms) return { pose: null, inferenceMs }
    const width = 'videoWidth' in video ? video.videoWidth : video.width
    const height = 'videoHeight' in video ? video.videoHeight : video.height
    if (!this.frame || this.frame.landmarks.length !== lms.length * 3) {
      this.frame = {
        landmarks: new Float32Array(lms.length * 3),
        visibility: new Float32Array(lms.length),
        worldLandmarks: new Float32Array(lms.length * 3),
        width,
        height,
        timestamp: timestampMs,
      }
    }
    pack(this.frame, lms)
    const world = result.worldLandmarks[0]
    if (world) {
      const w = this.frame.worldLandmarks!
      for (let i = 0; i < world.length; i++) {
        w[i * 3] = world[i]!.x
        w[i * 3 + 1] = world[i]!.y
        w[i * 3 + 2] = world[i]!.z
      }
    }
    this.frame.width = width
    this.frame.height = height
    this.frame.timestamp = timestampMs
    return { pose: this.frame, inferenceMs }
  }

  close(): void {
    this.landmarker?.close()
    this.landmarker = null
  }
}

function pack(frame: PoseFrame, lms: NormalizedLandmark[]): void {
  const out = frame.landmarks
  const vis = frame.visibility!
  for (let i = 0; i < lms.length; i++) {
    const l = lms[i]!
    out[i * 3] = l.x
    out[i * 3 + 1] = l.y
    out[i * 3 + 2] = l.z
    vis[i] = l.visibility
  }
}

export { POSE_LM }
